import { t } from '@lingui/core/macro'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentFavoritesRepository } from 'features/knowledgeDocumentViewer/hooks/useDocumentFavoritesRepository'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import {
  type DocumentFavorite,
  type FavoriteOperation,
  favoriteReferenceId,
} from 'features/knowledgeDocumentViewer/utils/documentFavoritesState'
import {
  FavoriteDocument,
  favoriteReferenceMatchesRow,
  resolveFavoritesForActiveDocuments,
} from 'features/knowledgeDocumentViewer/utils/favoriteDocumentsStorage'
import { useWorkOrders } from 'features/knowledgeDocumentViewer/workOrders/useWorkOrder'
import {
  isCmmsRecordId,
  workOrderRowFromEntry,
} from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo } from 'react'

function documentTableRowToFavorite(
  doc: DocumentTableRow
): DocumentFavorite | undefined {
  if (!doc.systemEntryId || (!doc.bqDocumentId && !isCmmsRecordId(doc.id)))
    return undefined
  return {
    reference: isCmmsRecordId(doc.id)
      ? { kind: 'cmms_record', entry_id: doc.id }
      : { kind: 'document', document_id: doc.bqDocumentId ?? doc.id },
    favorited_at: Date.now(),
  }
}

interface UseFavoriteDocumentsReturn {
  favoriteDocuments: FavoriteDocument[]
  isSaving: boolean
  isReady: boolean
  isFavorited: (id: string) => boolean
  toggleFavorite: (doc: DocumentTableRow) => void
  removeFavorite: (id: string) => void
}

export function useFavoriteDocuments(
  siteId?: string
): UseFavoriteDocumentsReturn {
  const {
    repository,
    favorites: favoriteDocuments,
    isSaving,
    isReady,
  } = useDocumentFavoritesRepository(siteId)
  const { errorNotification } = useGeckoNotifications()
  const save = useCallback(
    (operation: FavoriteOperation) => {
      if (!repository || !isReady) return
      void repository.mutate(operation).catch(() => {
        errorNotification(
          t`Could not save document favorites. Please try again.`
        )
      })
    },
    [errorNotification, isReady, repository]
  )

  const documentIds = useMemo(
    () =>
      favoriteDocuments
        .filter((favorite) => favorite.reference.kind !== 'cmms_record')
        .map((favorite) => favoriteReferenceId(favorite.reference)),
    [favoriteDocuments]
  )
  const cmmsIds = useMemo(
    () =>
      favoriteDocuments
        .filter((favorite) => favorite.reference.kind === 'cmms_record')
        .map((favorite) => favoriteReferenceId(favorite.reference)),
    [favoriteDocuments]
  )
  const { documents: activeDocuments } = useSystemDocumentsByBqIds(documentIds)
  const { entries: workOrders } = useWorkOrders(cmmsIds)

  const activeFavoriteDocuments = useMemo(
    () =>
      resolveFavoritesForActiveDocuments({
        favoriteDocuments,
        activeDocuments: activeDocuments ?? [],
        workOrderDocuments: workOrders.map(workOrderRowFromEntry),
      }),
    [activeDocuments, favoriteDocuments, workOrders]
  )

  const favoritedIds = useMemo(
    () => new Set(activeFavoriteDocuments.map((f) => f.id)),
    [activeFavoriteDocuments]
  )

  const isFavorited = useCallback(
    (id: string) => favoritedIds.has(id),
    [favoritedIds]
  )

  const toggleFavorite = useCallback(
    (doc: DocumentTableRow) => {
      const favorite = documentTableRowToFavorite(doc)
      if (!favorite || !repository) return
      const existing = repository
        .getSnapshot()
        .favorites.find((item) =>
          favoriteReferenceMatchesRow(item.reference, doc)
        )
      save(
        existing
          ? { type: 'remove', reference: existing.reference }
          : { type: 'add', favorite }
      )
    },
    [repository, save]
  )

  const removeFavorite = useCallback(
    (id: string) => {
      const resolved = activeFavoriteDocuments.find(
        (favorite) => favorite.id === id
      )
      if (resolved) save({ type: 'remove', reference: resolved.reference })
    },
    [activeFavoriteDocuments, save]
  )

  return {
    favoriteDocuments: activeFavoriteDocuments,
    isSaving,
    isReady,
    isFavorited,
    toggleFavorite,
    removeFavorite,
  }
}
